import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { EditWarningPropsTypes } from './EditWarningTypes'
import InputGroup from '../inputComp/InputGroup'
import { DescriptionIcon, EarlyWarningIcon, IdIcon } from '../iconSet/IconSet'
import useInputValidate from '../../hooks/useInputValidate'
import { SelectItemTypes } from '../../CommonTypes'
import Container from '../containerComp/Container'
import { InputGroupPropsTypes } from '../inputComp/InputGroupType'
import { getRefreshToken } from '../../utils/APIUtils'
import { APICall, INTERNAL_SERVER_ERROR, RECODE_NOT_FOUND } from '../../services/APIService'
import { API_EARLY_WARNING_DELETE_BY_VALUE_URL, API_GET_ALL_EARLY_WARNING_TYPES_URL, API_GET_ALL_EARLY_WARNINGS_URL, API_GET_WARNING_BY_VALUE_URL, API_UPDATE_WARNING_URL, APIRequestDeleteEarlyWarningByValueTypes, APIRequestGetEarlyWaringByValueTypes, APIRequestUpdateWarningTypes, APIResponseGetEarlyWarningsTypes, APIResponseGetWarningTypeTypes } from '../../services/APIEarlyWarningService'
import { useAlert } from '../../context/AlertContext'

const EditWarning = (props: EditWarningPropsTypes) => {

    const { showAlert } = useAlert()

    const [warnings, setWarnings] = useState<SelectItemTypes[]>([
        {value: 'landslide_warning', name: 'Landslide Warning'},
        {value: 'flood_warning', name: 'Flood Warning'}
    ])

    const [warningTypes, setWarningTypes] = useState<SelectItemTypes[]>([
        {value: 'landslide_warning', name: 'Landslide Warning'},
        {value: 'flood_warning', name: 'Flood Warning'}
    ])
    
    // Loading screen
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const warningRef = useRef<HTMLSelectElement | null>(null)
    const warningTypeRef = useRef<HTMLSelectElement | null>(null)
    const warningNameRef = useRef<HTMLInputElement | null>(null)
    const warningDescriptionRef = useRef<HTMLTextAreaElement | null>(null)

    // Handle warning change
    const handleWarningChange = async (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {

        if(!warningRef.current || !warningTypeRef.current || !warningNameRef.current || !warningDescriptionRef.current)
        {
            console.log('empty data field')
            showAlert('empty data field', 'ERROR')
            setIsLoading(false)
            return
        }

        const requestBody: APIRequestGetEarlyWaringByValueTypes = {
            value: warningRef.current.value
        }

        try
        {
            const response = await APICall(API_GET_WARNING_BY_VALUE_URL, 'POST', requestBody)

            if(response.httpStatus === 'OK')
            {
                const requestData: APIResponseGetEarlyWarningsTypes = response.data

                warningTypeRef.current.value = requestData.type.id
                warningNameRef.current.value = requestData.name
                warningDescriptionRef.current.value = requestData.description
            }
            else
            {
                const errorMessages: {[key: string]: string} = {
                    [RECODE_NOT_FOUND]: 'no any early warning found'
                }

                const errorMessage = response.errorCode && errorMessages[response.errorCode] ? errorMessages[response.errorCode] : 'Unknown error'
                console.log(errorMessage);
                showAlert(errorMessage, 'ERROR');
            }
        }
        catch(error)
        {
            console.log('data fetching error');
            showAlert('data fetching error', 'ERROR')
        }

        setIsLoading(false)
    }


    const formItems: InputGroupPropsTypes[] = [
        {
            type: 'select',
            icon: <EarlyWarningIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            label: 'warning',
            componentSelectElement: warningRef,
            required: true,
            optionList: warnings,
            placeholder: 'select warning',
            onChange: handleWarningChange
        },
        {
            type: 'select',
            icon: <EarlyWarningIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            label: 'warning type',
            componentSelectElement: warningTypeRef,
            required: true,
            optionList: warningTypes,
            placeholder: 'Select warning type',
        },
        {
            type: 'text',
            icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter warning name',
            label: 'warning name',
            componentInputElement: warningNameRef,
        },
        {
            type: 'text-area',
            icon: <DescriptionIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter warning description',
            label: 'warning description',
            componentTextArea: warningDescriptionRef,
        }
    ]

    // Custom hook
    const {validateField} = useInputValidate()

    // Update details button click
    const handleUpdateDetailsClick = async () => {
        const warningType = validateField(warningTypeRef)

        setIsLoading(true)

        // Get new access token
        getRefreshToken()

        const accessToken: string | null = window.localStorage.getItem("accessToken")

        if(!accessToken)
        {
            console.log('access token not found')
            showAlert('access token not found', 'ERROR')
            setIsLoading(false)
            return
        }

        if(!warningRef.current || !warningTypeRef.current || !warningNameRef.current || !warningDescriptionRef.current)
        {
            console.log('empty field directed')
            showAlert('empty field directed', 'ERROR')
            setIsLoading(false)
            return
        }

        const requestBody: APIRequestUpdateWarningTypes = {
            accessToken: accessToken,
            id: warningRef.current.value,
            type: warningTypeRef.current.value,
            name: warningNameRef.current.value,
            description: warningDescriptionRef.current.value
        }

        try
        {
            const response = await APICall(API_UPDATE_WARNING_URL, 'POST', requestBody)

            if(response.httpStatus === 'OK')
            {
                warningRef.current.value = ''
                warningTypeRef.current.value = ''
                warningNameRef.current.value = ''
                warningDescriptionRef.current.value = ''

                getAllWarnings()

                console.log('early warning updated')
                showAlert('early warning updated successfully', 'SUCCESS')
            }
            else 
            {
                const errorMessages: {[key: string]: string} = {
                    [INTERNAL_SERVER_ERROR]: 'internal server error',
                    [RECODE_NOT_FOUND]: 'selected early warning type not exists'
                }

                const errorMessage = response.errorCode && errorMessages[response.errorCode] ? errorMessages[response.errorCode] : 'Unknown error'
                console.log(errorMessage)
                showAlert(errorMessage, 'ERROR')
            }
        }
        catch(error)
        {
            console.log('warning server not responding')
            showAlert('warning server not responding', 'ERROR')
        }

        setIsLoading(false)
    }

    // Delete button click
    const handleDeleteClick = async () => {
        setIsLoading(true)
        // Get new access token
        getRefreshToken()

        const accessToken: string | null = window.localStorage.getItem("accessToken")

        if(!accessToken)
        {
            console.log('logout')
            window.localStorage.clear()
            showAlert('session expired', 'ERROR')
            window.location.href = '/'
            setIsLoading(false)
            return
        }

        if(!warningRef.current || !warningTypeRef.current || !warningNameRef.current || !warningDescriptionRef.current)
        {
            console.log('empty field directed')
            showAlert('empty field directed', 'ERROR')
            setIsLoading(false)
            return
        }

        const requestBody: APIRequestDeleteEarlyWarningByValueTypes = {
            accessToken: accessToken,
            value: warningRef.current.value
        }

        try
        {
            const response = await APICall(API_EARLY_WARNING_DELETE_BY_VALUE_URL, 'POST', requestBody)

            if(response.httpStatus === 'OK')
            {
                warningRef.current.value = ''
                warningTypeRef.current.value = ''
                warningNameRef.current.value = ''
                warningDescriptionRef.current.value = ''

                getAllWarnings()

                console.log('early warning deleted')
                showAlert('early warning deleted successfully', 'SUCCESS')
            }
            else
            {
                const errorMessages: {[key: string]: string} = {
                    [INTERNAL_SERVER_ERROR]: 'internal server error',
                    [RECODE_NOT_FOUND]: 'recode not found to delete'
                }

                const errorMessage = response.errorCode && errorMessages[response.errorCode] ? errorMessages[response.errorCode] : 'Unknown error'
                console.log(errorMessage)
                showAlert(errorMessage, 'ERROR')
            }
        }
        catch(error)
        {
            console.log('data fetching error');
            showAlert('data fetching error', 'ERROR')
        }

        setIsLoading(false)
    }

    // Load available warnings
    const getAllWarnings = async () => {
        try{
            const response = await APICall(API_GET_ALL_EARLY_WARNINGS_URL, 'GET', null)

            if(response.httpStatus === 'OK'){
                const responseData: APIResponseGetEarlyWarningsTypes[] = response.data
                const fetchedWarningType: SelectItemTypes[] = []

                responseData.map(item => {
                    fetchedWarningType.push({value: item.id, name: item.name})
                })

                setWarnings(fetchedWarningType)
            }
        }
        catch(error){
            console.log('server not responding')
        }
    }

    // Load all available warning types
    const getAllWarningTypes = async () => {
        try
        {
            const response = await APICall(API_GET_ALL_EARLY_WARNING_TYPES_URL, 'GET')

            if(response.httpStatus === 'OK'){
                const responseData: APIResponseGetWarningTypeTypes[] = response.data
                const fetchedEarlyWarningTypes: SelectItemTypes[] = []

                responseData.map(item => {
                    fetchedEarlyWarningTypes.push({value: item.id, name: item.type})
                })

                setWarningTypes(fetchedEarlyWarningTypes)
            }
            else
            {
                console.log('data fetching error')
            }
        }
        catch(error)
        {
            console.log('data fetching error')
        }
    }
    
    useEffect(() => {
        setIsLoading(true)
        getAllWarningTypes()
        getAllWarnings()
        setIsLoading(false)
    }, [])
    
    return (
        <Container title='edit warning' isLoading={isLoading}>
            {/* Customization -> edit -> edit warning */}
            <div className={`edit-warning`}>
                <div className={`grid-layout-edit-warning`}>
                    <div className={`left-side`}>
                        {formItems.slice(0, 4).map((field, index) => (
                            <InputGroup key={index} {...field}/>
                        ))}
                    </div>

                    <div className={`right-side`}></div>

                    <div className={`button-wrapper`} style={{gridArea: 'button'}}>
                        <div className={`left-side`}></div>
                        <div className={`right-side`}>
                            <button className={`button-normal`} style={{backgroundColor: 'var(--color-main-red)'}} onClick={(e) => handleDeleteClick()}>delete</button>
                            <button className={`button-normal`} onClick={(e) => handleUpdateDetailsClick()}>update details</button>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    )
}

export default EditWarning